import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flattenGroups, normalizeOptionGroup, type OptionProp, type Options, type OptionsGroup } from '../../shared/normalize-options';

type UseTypeaheadOptionsParams = {
    options: Options;
    value?: string;
    preserveSelectedOption?: boolean;
};

type UseTypeaheadOptionsResult = {
    normalizedGroups: OptionsGroup;
    flattenedOptions: OptionProp[];
    optionIndexByValue: Map<string, number>;
    selectedOption?: OptionProp;
    activeIndex: number;
    selectOption: (option: OptionProp) => void;
    selectOptionByValue: (value: string) => void;
    clearSelection: () => void;
    findOptionByValue: (value?: string) => OptionProp | undefined;
};

const buildOptionIndexMap = (options: OptionProp[]) => {
    const indexMap = new Map<string, number>();
    options.forEach((option, index) => {
        indexMap.set(option.value, index);
    });
    return indexMap;
};

const useTypeaheadOptions = ({ options, value, preserveSelectedOption = false }: UseTypeaheadOptionsParams): UseTypeaheadOptionsResult => {
    const normalizedGroups = useMemo(() => normalizeOptionGroup(options), [options]);
    const flattenedOptions = useMemo(() => flattenGroups(normalizedGroups), [normalizedGroups]);
    const optionIndexByValue = useMemo(() => buildOptionIndexMap(flattenedOptions), [flattenedOptions]);

    const findOptionByValue = useCallback(
        (candidate?: string) => {
            if (!candidate) return undefined;
            return flattenedOptions.find((option) => option.value === candidate);
        },
        [flattenedOptions]
    );

    const [selectedOption, setSelectedOption] = useState<OptionProp | undefined>(() => findOptionByValue(value));
    const [activeIndex, setActiveIndex] = useState<number>(() => {
        if (!value) return -1;
        return optionIndexByValue.get(value) ?? -1;
    });

    const lastValueRef = useRef(value);

    useEffect(() => {
        if (value === lastValueRef.current) return;
        lastValueRef.current = value;

        if (typeof value === 'undefined') {
            setSelectedOption(undefined);
            setActiveIndex(-1);
            return;
        }

        const matched = findOptionByValue(value);
        if (matched) {
            setSelectedOption(matched);
            setActiveIndex(optionIndexByValue.get(matched.value) ?? -1);
            return;
        }

        if (!preserveSelectedOption) {
            setSelectedOption(undefined);
        }
        setActiveIndex(-1);
    }, [findOptionByValue, optionIndexByValue, preserveSelectedOption, value]);

    useEffect(() => {
        if (!selectedOption) return;
        const index = optionIndexByValue.get(selectedOption.value);
        if (typeof index === 'undefined') {
            if (!preserveSelectedOption) {
                setSelectedOption(undefined);
            }
            setActiveIndex(-1);
        } else {
            setActiveIndex(index);
        }
    }, [optionIndexByValue, preserveSelectedOption, selectedOption]);

    const selectOption = useCallback(
        (option: OptionProp) => {
            setSelectedOption(option);
            setActiveIndex(optionIndexByValue.get(option.value) ?? -1);
        },
        [optionIndexByValue]
    );

    const selectOptionByValue = useCallback(
        (nextValue: string) => {
            const option = findOptionByValue(nextValue);
            if (option) {
                selectOption(option);
                return;
            }
            if (!preserveSelectedOption) {
                setSelectedOption(undefined);
            }
            setActiveIndex(-1);
        },
        [findOptionByValue, preserveSelectedOption, selectOption]
    );

    const clearSelection = useCallback(() => {
        setSelectedOption(undefined);
        setActiveIndex(-1);
    }, []);

    return {
        normalizedGroups,
        flattenedOptions,
        optionIndexByValue,
        selectedOption,
        activeIndex,
        selectOption,
        selectOptionByValue,
        clearSelection,
        findOptionByValue,
    };
};

export type { UseTypeaheadOptionsParams, UseTypeaheadOptionsResult };
export { useTypeaheadOptions };
